/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { useState, useEffect, useMemo } from "react";
import {
  Tabs,
  Tab,
  Typography,
  Button,
  CircularProgress,
  Box,
  Chip,
  Alert,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Grid from "@mui/material/Grid2";
import { useQueryClient } from "@tanstack/react-query";
import DocumentDetail from "./DocumentDetail";
import useGetResource from "@/hooks/useGetResources";
import useGetDetailledDocument from "@/hooks/useGetDetailledDocument";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useTranslation } from "react-i18next";

// Import shared components
import DocumentCard from "./components/shared/DocumentCard";
import MapDialog from "./components/shared/MapDialog";
import CalendarDialog from "./components/shared/CalendarDialog";
import DocumentsHeader from "./components/shared/DocumentsHeader";
import DocumentSummaryPopup from "./components/shared/DocumentSummaryPopup";

function a11yProps(index) {
  return {
    id: `tab-${index}`,
    "aria-controls": `tabpanel-${index}`,
  };
}

// TabPanel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// MyDocuments Component
function MyDocuments() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isLgDown = useMediaQuery(theme.breakpoints.down("lg"));
  const queryClient = useQueryClient();
  const [pageTokens, setPageTokens] = useState([null]);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pageLimit, setPageLimit] = useState(20);
  const [nextPageToken, setNextPageToken] = useState(null);
  const { toast, setToast, handleToastClose } = useApiErrorHandler();
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [summaryPopupOpen, setSummaryPopupOpen] = useState(false);
  const [summaryDocument, setSummaryDocument] = useState(null);

  const currentPageToken = pageTokens[currentPageIndex];
  const queryUrl = currentPageToken
    ? `/api/v1/users/my-work-permits?cursor=${currentPageToken}`
    : `/api/v1/users/my-work-permits`;

  const {
    data: myDocuments,
    isLoading,
    isFetching,
    error,
    refetch,
    isPlaceholderData,
    isError,
  } = useGetResource(["mydocuments", currentPageToken], queryUrl, {
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: "always", // Always refetch when component mounts
  });

  const { getDetailledData } = useGetDetailledDocument();

  useEffect(() => {
    if (isPlaceholderData) return;
    if (isError) return;
    if (!myDocuments || !Array.isArray(myDocuments.records)) return;

    const sanitized = myDocuments.records.filter((r) => r && r.id);

    if (currentPageToken == null) {
      setPages([sanitized]);
      setCurrentPageIndex(0);
      setTotalCount(
        typeof myDocuments.total_count === "number"
          ? myDocuments.total_count
          : Number(myDocuments.total_count) || 0
      );
      setPageLimit(myDocuments.page_limit || 20);
    } else {
      setPages((prev) => {
        const existingIds = new Set(
          prev.flat().filter(Boolean).map((r) => r.id).filter(Boolean)
        );
        const filteredRecords = sanitized.filter((r) => !existingIds.has(r.id));
        const newPages = [...prev];
        while (newPages.length <= currentPageIndex) {
          newPages.push([]);
        }
        newPages[currentPageIndex] = filteredRecords;
        return newPages;
      });
    }

    setNextPageToken(myDocuments.next_page ?? null);

    if (myDocuments.next_page) {
      setPageTokens((prev) => {
        if (prev.includes(myDocuments.next_page)) return prev;
        return [...prev, myDocuments.next_page];
      });
    }
  }, [
    myDocuments,
    currentPageIndex,
    currentPageToken,
    isPlaceholderData,
    isError,
  ]);

  const aggregatedDocuments = useMemo(() => {
    const flat = pages
      .slice(0, currentPageIndex + 1)
      .flat()
      .filter((doc) => doc && doc.id);
    const seen = new Set();
    return flat.filter((doc) => {
      if (seen.has(doc.id)) return false;
      seen.add(doc.id);
      return true;
    });
  }, [pages, currentPageIndex]);

  const hasPartialResults =
    totalCount > 0 && aggregatedDocuments.length < totalCount;

  const statusTabs = useMemo(() => {
    if (!aggregatedDocuments.length) return [];
    const allTab = [t("myDocuments.allTab"), aggregatedDocuments];
    const statusMap = new Map();
    aggregatedDocuments.forEach((doc) => {
      const lastStep = doc.history_steps?.length
        ? doc.history_steps[doc.history_steps.length - 1]
        : null;
      const raw = lastStep?.status ?? "";
      const normalizedStatus = String(raw).trim().toLowerCase() || "unknown";
      if (!statusMap.has(normalizedStatus)) {
        statusMap.set(normalizedStatus, []);
      }
      statusMap.get(normalizedStatus).push(doc);
    });
    const statusTabsArray = Array.from(statusMap.entries());
    statusTabsArray.sort((a, b) => a[0].localeCompare(b[0]));
    return [allTab, ...statusTabsArray];
  }, [aggregatedDocuments, t]);

  const selectedDocuments = useMemo(() => {
    return statusTabs[currentTab]?.[1] || [];
  }, [statusTabs, currentTab]);

  const markerLocations = useMemo(() => {
    return selectedDocuments
      .filter(
        (doc) =>
          doc.id &&
          doc.site &&
          Number.isFinite(doc.site.latitude) &&
          Number.isFinite(doc.site.longitude)
      )
      .map((doc) => ({
        key: doc.id,
        location: {
          lat: doc.site.latitude,
          lng: doc.site.longitude,
        },
      }));
  }, [selectedDocuments]);

  const handleTabChange = (e, newValue) => {
    setCurrentTab(newValue);
  };

  const handleNextPage = () => {
    if (!nextPageToken || isFetching) return;

    const slotEmpty =
      !pages[currentPageIndex] || pages[currentPageIndex]?.length === 0;
    if (currentPageIndex > 0 && slotEmpty && isError) {
      refetch();
      return;
    }

    const nextIdx = currentPageIndex + 1;
    if (nextIdx >= pageTokens.length) return;

    setCurrentPageIndex(nextIdx);
  };

  const handleCalendarEventClick = (document) => {
    // CalendarDialog passes the full document object
    if (document) {
      setSummaryDocument(document);
      setSummaryPopupOpen(true);
    }
  };

  const handleCloseSummaryPopup = () => {
    setSummaryPopupOpen(false);
    setSummaryDocument(null);
  };

  const handleViewDetailsFromSummary = (documentId) => {
    handleDetailClick(documentId);
    handleCloseSummaryPopup();
  };

  const handleMapMarkerClick = (documentId) => {
    // Close map and open document detail
    handleCloseMapDialog();
    handleDetailClick(documentId);
  };

  const handleDetailClick = async (documentId) => {
    setLoadingStates((prev) => ({ ...prev, [documentId]: true }));
    try {
      const data = await getDetailledData(documentId);
      setSelectedDocument(data);
    } catch (error) {
      const apiMsg = error.response?.data?.message;

      // Your generic translation, then the API text
      const fullMessage = apiMsg
        ? `${t("myDocuments.errorLoadDetails")}: ${apiMsg}`
        : t("myDocuments.errorLoadDetails");

      setToast({
        open: true,
        severity: "error",
        message: fullMessage,
      });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [documentId]: false }));
    }
  };

  const handleOpenMapDialog = () => {
    setMapDialogOpen(true);
  };

  const handleCloseMapDialog = () => {
    setMapDialogOpen(false);
  };

  const handleOpenCalendar = () => {
    setCalendarDialogOpen(true);
  };

  const handleCloseCalendar = () => {
    setCalendarDialogOpen(false);
  };

  const hasMoreDocuments = Boolean(nextPageToken);

  if (isLoading && pages.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        borderRadius={2}
      >
        <CircularProgress
          size={48}
          aria-label={t("myDocuments.loadingDocumentsAria")}
        />
        <Typography variant="body1" ml={2} color="text.secondary">
          {t("myDocuments.loadingDocuments")}
        </Typography>
      </Box>
    );
  }

  if (error && aggregatedDocuments.length === 0 && !isFetching) {
    return (
      <Box p={3} bgcolor="error.light" borderRadius={2} textAlign="center">
        <Typography variant="h6" color="error.main">
          {t("myDocuments.errorTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("myDocuments.errorMessage")}
        </Typography>
        <Button
          variant="contained"
          color="error"
          onClick={() => refetch()}
          sx={{ mt: 2, textTransform: "none" }}
        >
          {t("myDocuments.retryButton") || "Retry"}
        </Button>
      </Box>
    );
  }

  if (!aggregatedDocuments.length && !error) {
    return (
      <Box p={3} borderRadius={2} textAlign="center">
        <Typography variant="h6" color="text.primary">
          {t("myDocuments.noDocumentsTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("myDocuments.noDocumentsMessage")}
        </Typography>
      </Box>
    );
  }

  if (!aggregatedDocuments.length && error && isFetching) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        borderRadius={2}
      >
        <CircularProgress
          size={48}
          aria-label={t("myDocuments.loadingDocumentsAria")}
        />
        <Typography variant="body1" ml={2} color="text.secondary">
          {t("myDocuments.loading")}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: theme.palette.background.default,
        }}
      >
        {/* Compact Enterprise Header */}
        <DocumentsHeader
          icon={<AssignmentIcon />}
          title={t("myDocuments.title") || "Work Permits"}
          subtitle={
            t("myDocuments.subtitle") ||
            "Manage and track your permit to work requests"
          }
          shownCount={aggregatedDocuments.length}
          totalCount={totalCount || aggregatedDocuments.length}
          onCalendarClick={handleOpenCalendar}
          onMapClick={handleOpenMapDialog}
          bannerNote={
            hasPartialResults
              ? t("myDocuments.partialResultsNotice")
              : undefined
          }
          t={t}
          translationPrefix="myDocuments"
        />

        {error && aggregatedDocuments.length > 0 && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => refetch()}
                sx={{ fontWeight: 600, textTransform: "none" }}
              >
                {t("myDocuments.retryButton")}
              </Button>
            }
          >
            {t("myDocuments.loadMoreErrorInline")}
          </Alert>
        )}

        {/* Enhanced Tabs Section */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            p: 1,
            mb: 3,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
          }}
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label={t("myDocuments.statusTabsAria")}
            variant={isLgDown ? "standard" : "scrollable"}
            scrollButtons={isLgDown ? false : "auto"}
            allowScrollButtonsMobile={!isLgDown}
            sx={{
              "& .MuiTabs-flexContainer": {
                gap: 1,
                flexWrap: isLgDown ? "wrap" : "nowrap",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minHeight: 48,
                borderRadius: 1.5,
                margin: "4px 0",
                transition: "all 0.2s ease",
                justifyContent: "center",
                maxWidth: isLgDown ? "none" : undefined,
                minWidth: isLgDown ? "auto" : undefined,
                paddingLeft: isLgDown ? 1.5 : undefined,
                paddingRight: isLgDown ? 1.5 : undefined,
                flex: isLgDown ? "0 1 auto" : "0 0 auto",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
                "&.Mui-selected": {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                },
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            {statusTabs.map(([tabName, docs], index) => (
              <Tab
                key={tabName}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="inherit">{tabName}</Typography>
                    <Chip
                      label={docs.length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        bgcolor:
                          currentTab === index
                            ? theme.palette.primary.main
                            : alpha(theme.palette.text.secondary, 0.1),
                        color:
                          currentTab === index
                            ? theme.palette.primary.contrastText
                            : theme.palette.text.secondary,
                      }}
                    />
                  </Box>
                }
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        </Box>

        {statusTabs.map(([tabName, docs], index) => (
          <TabPanel key={tabName} value={currentTab} index={index}>
            {docs.length > 0 ? (
              <Grid container spacing={3}>
                {docs.map((doc) => (
                  <Grid
                    key={doc.id}
                    size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }}
                  >
                    <DocumentCard
                      doc={doc}
                      onDetailClick={handleDetailClick}
                      isLoading={!!loadingStates[doc.id]}
                      showDeleteButton={false}
                      showCreatedAt={false}
                      showBusinessIcon={true}
                      t={t}
                      translationPrefix="myDocuments"
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  px: 3,
                  bgcolor: theme.palette.background.paper,
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                }}
              >
                <AssignmentIcon
                  sx={{
                    fontSize: 64,
                    color: theme.palette.text.disabled,
                    mb: 2,
                  }}
                />
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  {t("myDocuments.noDocumentsInStatus") ||
                    `No ${tabName.toLowerCase()} permits`}
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {t("myDocuments.noDocumentsInStatusDescription") ||
                    "No permits found for this status"}
                </Typography>
              </Box>
            )}
          </TabPanel>
        ))}

        {currentTab === 0 && hasMoreDocuments && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 4,
              p: 3,
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleNextPage}
              disabled={!nextPageToken || isFetching}
              aria-label={t("myDocuments.loadMoreAria")}
              startIcon={
                isFetching ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: theme.shadows[2],
                "&:hover": {
                  boxShadow: theme.shadows[4],
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {isFetching
                ? t("myDocuments.loading") || "Loading..."
                : t("myDocuments.loadMore") || "Load More Permits"}
            </Button>
          </Box>
        )}

        {selectedDocument && (
          <DocumentDetail
            workPermit={selectedDocument}
            onClose={() => setSelectedDocument(null)}
            onActionSuccess={() => {
              setPageTokens([null]);
              setPages([]);
              setCurrentPageIndex(0);
              setTotalCount(0);
              setNextPageToken(null);
              queryClient.invalidateQueries(["mydocuments"]);
              setToast({
                open: true,
                severity: "success",
                message: t("myDocuments.actionSuccess"),
              });
            }}
          />
        )}

        <MapDialog
          open={mapDialogOpen}
          onClose={handleCloseMapDialog}
          documents={selectedDocuments}
          markerLocations={markerLocations}
          onMarkerClick={handleMapMarkerClick}
          mapsLoaded={mapsLoaded}
          setMapsLoaded={setMapsLoaded}
          t={t}
          translationPrefix="myDocuments"
        />

        <CalendarDialog
          open={calendarDialogOpen}
          onClose={handleCloseCalendar}
          documents={selectedDocuments}
          onSelectEvent={handleCalendarEventClick}
          t={t}
          i18nLanguage={i18n.language}
          translationPrefix="myDocuments"
        />

        <DocumentSummaryPopup
          open={summaryPopupOpen}
          onClose={handleCloseSummaryPopup}
          document={summaryDocument}
          onViewDetails={handleViewDetailsFromSummary}
          t={t}
        />

        <Toast
          open={toast.open}
          onClose={handleToastClose}
          severity={toast.severity}
          message={toast.message}
        />
      </Box>
    </>
  );
}

export default MyDocuments;
